import {
  IGridFilter,
  IArrayGridFilter,
  IGridFilterDefinition,
  GridFilterDefinitions,
  toGridDefinitionMap,
  customFieldToFilterDefinition,
  RestLayerFilterFactory,
  defaultOperatorFactories,
  hasFilterValue,
  expandFilterValue,
  ITextGridFilter,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { MISSING_SESSION_ID_STATE_PARAM } from "./controllers/all";
import { localize } from "@gtmhub/localization";
import { ICustomFieldsAllTypesMap } from "@gtmhub/customFields";
import { getCurrentUserId } from "@gtmhub/users/current-user-storage";
import { escapeString } from "@gtmhub/util/utils";

export const filterFieldNames = Object.freeze({
  tags: "tags.title",
});

export const generateDefaultFilters = (planningSessionId: string): IGridFilter[] => {
  const filters: IGridFilter[] = [];

  if (planningSessionId) {
    const sessionFilter: IGridFilter = {
      fieldName: "sessionId",
      dataType: "array",
      operator: "isOneOf",
      value: planningSessionId === MISSING_SESSION_ID_STATE_PARAM ? [] : [planningSessionId],
    };
    filters.push(sessionFilter);
  } else {
    throw new Error("Missing session");
  }

  const ownerFilter: IGridFilter = {
    fieldName: "ownerIds",
    dataType: "array",
    operator: "contains",
    value: [],
  };

  filters.push(ownerFilter);

  const tagFilter: IGridFilter = {
    fieldName: filterFieldNames.tags,
    dataType: "array",
    operator: "contains",
    value: [],
  };

  filters.push(tagFilter);

  return filters;
};

export const goalsFilterDefinitions = (args: {
  customFields: ICustomFieldsAllTypesMap;
  planningSessionId: string;
  isNestedView?: boolean;
  isDesignScoreGridEnabled: boolean;
}): GridFilterDefinitions => {
  const customFieldDefinitions = customFieldFilterDefinitions(args);
  const baseDefinitions: IGridFilterDefinition[] = [];

  baseDefinitions.push({
    fieldName: "sessionId",
    displayName: localize("session_cap"),
    trackingName: "Session",
    dataType: "array",
    arrayType: "session",
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
    readonly: args.isNestedView,
    hostSessionId: args.planningSessionId,
  });

  baseDefinitions.push(
    {
      fieldName: "ownerIds",
      displayName: localize("owners_cap"),
      trackingName: "Owners",
      dataType: "array",
      arrayType: "assignee",
      defaultOperator: "contains",
      supportedOperators: ["contains"],
    },
    {
      fieldName: "metrics.ownerIds",
      displayName: localize("kr_owner"),
      trackingName: "KR owners",
      dataType: "array",
      arrayType: "assignee",
      defaultOperator: "contains",
      supportedOperators: ["contains"],
    },
    {
      fieldName: "name",
      displayName: localize("title_cap"),
      trackingName: "Title",
      dataType: "text",
      defaultOperator: "contains",
      supportedOperators: ["contains"],
      placeholder: localize("search_by_title_of_okr"),
    }
  );

  baseDefinitions.push(
    {
      fieldName: filterFieldNames.tags,
      displayName: localize("tags"),
      trackingName: "Tags",
      dataType: "array",
      arrayType: "tag",
      defaultOperator: "contains",
      supportedOperators: ["contains"],
    },
    {
      fieldName: "attainment",
      displayName: localize("progress_cap"),
      trackingName: "Progress",
      dataType: "numeric",
      defaultOperator: "equals",
      supportedOperators: args.isDesignScoreGridEnabled ? ["equals", "notEqual", "greater", "less"] : ["equals", "greater", "less"],
      percentage: true,
    },
    {
      fieldName: "metrics.latestSnapshotAt",
      displayName: localize("last_updated"),
      trackingName: "Last updated",
      dataType: "date",
      defaultOperator: "isBefore",
      supportedOperators: ["isBefore", "isAfter", "isBetween", "isNotBetween"],
    }
  );

  if (args.isDesignScoreGridEnabled) {
    baseDefinitions.push({
      fieldName: "designScore.totalPoints",
      displayName: localize("okr-design-score"),
      trackingName: "Okr-Design Score",
      dataType: "numeric",
      defaultOperator: "equals",
      supportedOperators: ["equals", "notEqual", "greater", "less"],
    });
  }

  return toGridDefinitionMap([...baseDefinitions, ...customFieldDefinitions]);
};

export const customFieldFilterDefinitions = (args: { customFields: ICustomFieldsAllTypesMap }): IGridFilterDefinition[] => {
  // prepare custom field definitions
  const customFieldDefinitions: IGridFilterDefinition[] = [];
  const usedCustomFieldLabels = new Map<string, { targetType: string; definition: IGridFilterDefinition }[]>();

  const customFieldTypes: (keyof ICustomFieldsAllTypesMap)[] = ["goal", "metric", "user", "team"];

  for (const targetType of customFieldTypes) {
    for (const customField of args.customFields[targetType] || []) {
      const definition = customFieldToFilterDefinition(customField);
      if (!definition) {
        console.warn("Cannot create filter definition for custom field", customField);
        continue;
      }
      customFieldDefinitions.push(definition);
      addToCfMap(targetType, definition, usedCustomFieldLabels);
      setDefinitionFieldName(targetType, definition);
    }
  }

  enrichCfDisplayNames(usedCustomFieldLabels);

  return customFieldDefinitions;
};

const addToCfMap = (
  targetType: string,
  definition: IGridFilterDefinition,
  usedCustomFieldLabels: Map<string, { targetType: string; definition: IGridFilterDefinition }[]>
): void => {
  const lowerCaseDisplayName = definition.displayName.toLowerCase();
  const repeatingDefinitions = usedCustomFieldLabels.get(lowerCaseDisplayName);
  if (repeatingDefinitions) {
    repeatingDefinitions.push({ targetType, definition });
  } else {
    usedCustomFieldLabels.set(lowerCaseDisplayName, [{ targetType, definition }]);
  }
};

const setDefinitionFieldName = (targetType: string, definition: IGridFilterDefinition): void => {
  if (targetType === "metric") {
    definition.fieldName = `metrics.${definition.fieldName}`;
  }

  if (targetType === "user" || targetType === "team") {
    definition.fieldName = `assignees.${definition.fieldName}`;
  }
};

const enrichCfDisplayNames = (usedCustomFieldLabels: Map<string, { targetType: string; definition: IGridFilterDefinition }[]>): void => {
  usedCustomFieldLabels.forEach((definitions) => {
    if (definitions.length > 1) {
      for (const { targetType, definition } of definitions) {
        definition.displayName += ` (${translateCfType(targetType)})`;
      }
    }
  });
};

const translateCfType = (targetType: string): string => {
  const typeKeyMap = {
    goal: "objectives",
    metric: "key_results",
    user: "users",
    team: "teams",
  };

  return localize(typeKeyMap[targetType]);
};

export const separateDynamicValues = (filters): IGridFilter[] => {
  return filters.reduce((prev, current) => {
    if (current.fieldName.includes("ownerIds") && current.dynamicValues && current.dynamicValues.length) {
      const { fieldName, dataType, operator, value, dynamicValues } = current;
      let copyDynamicVal: IGridFilter[] = [];

      const checkMeTagDynamicVal = dynamicValues.findIndex((el) => el.type === "me");
      if (checkMeTagDynamicVal >= 0) {
        copyDynamicVal = [...copyDynamicVal, ...dynamicValues.splice(checkMeTagDynamicVal, 1)];
      }
      prev.push({ fieldName, dataType, operator, value, ...{ dynamicValues: copyDynamicVal } });

      dynamicValues.forEach((val) => {
        const nameDynamic = fieldName + "_" + val.type;

        // Check if filter has already been added and we need to add only another value
        const found = prev.findIndex((el) => el.fieldName === nameDynamic);
        if (found >= 0) {
          prev[found].value.push(val.teamId);
          return;
        }

        // Check if 'myTeams' tag has been used. Than add only userId
        if (val.type === "myTeams") {
          prev.push({
            fieldName: nameDynamic,
            value: [getCurrentUserId()],
            dataType,
            operator,
          });
          return;
        }

        prev.push({
          fieldName: nameDynamic,
          dataType,
          operator,
          value: [val.teamId],
        });
      });

      return prev;
    }
    return [...prev, current];
  }, []);
};

// to do: remove when we implement non-existent dynamic filter
// https://gtmhub.atlassian.net/browse/GVS-2095
export const filtersOverrideForSessionlessGoals = (): Record<string, RestLayerFilterFactory> => {
  return {
    sessionId: (filter: IArrayGridFilter) => {
      const defaultFactory = defaultOperatorFactories[filter.operator];
      // removing "none" (if present) from filter values to avoid bad request errors
      const sanitizedValue = filter.value.filter((sessionId) => sessionId !== MISSING_SESSION_ID_STATE_PARAM);

      return {
        $or: [{ sessionId: { $exists: false } }, defaultFactory(<IGridFilter>{ ...filter, value: sanitizedValue })],
      };
    },
  };
};

// to do: remove when we implement non-existent dynamic filter
// https://gtmhub.atlassian.net/browse/GVS-2095
export const processedFiltersForSessionlessGoals = (filters: IGridFilter[]): IGridFilter[] => {
  const sessionIdFieldName = "sessionId";
  const sessionIdFilter = <IArrayGridFilter>filters.find((filter) => filter.fieldName === sessionIdFieldName);
  // if (sessionIdFilter && !sessionIdFilter.value.length) {
  if (sessionIdFilter) {
    // this condition is not needed but adding in any case
    if (!Array.isArray(sessionIdFilter.value)) {
      sessionIdFilter.value = [];
    }
    if (!sessionIdFilter.value.length) {
      // if there is an empty session filter -> add value(though invalid) so it won't be skipped when evaluated
      // the filtersOverride will take care of it later
      sessionIdFilter.value.push(MISSING_SESSION_ID_STATE_PARAM);
    }
  } else {
    // if no session filter -> add a session filter(just for the rest layer method) so the restLayerRequest won't skip it
    const sessionIdFilter: IArrayGridFilter = {
      fieldName: sessionIdFieldName,
      dataType: "array",
      operator: "isOneOf",
      value: [MISSING_SESSION_ID_STATE_PARAM],
    };

    filters.push(sessionIdFilter);
  }

  return filters;
};

export const hasFiltersWithValues = (filters: IGridFilter[]): boolean => {
  const filtersWithValues = filters.filter((filter) => hasFilterValue(filter));
  return filtersWithValues.length > 0;
};

export const hasOnlyHostSessionFilter = (filters: IGridFilter[], hostSessionId: string): boolean => {
  if (hasOnlySessionFilter(filters)) {
    const sessionFilter = filters.filter((filter) => hasFilterValue(filter))[0] as IArrayGridFilter;
    const noDynamicValues = !sessionFilter.dynamicValues || sessionFilter.dynamicValues.length === 0;
    return sessionFilter.value.length === 1 && sessionFilter.value[0] === hostSessionId && noDynamicValues;
  } else {
    return false;
  }
};

export const hasOnlySessionFilter = (filters: IGridFilter[]): boolean => {
  const filtersWithValues = filters.filter((filter) => hasFilterValue(filter));
  return filtersWithValues.length === 1 && filtersWithValues[0].fieldName === "sessionId";
};

export const filtersOverrideForTagTitle = (): Record<string, RestLayerFilterFactory> => {
  return {
    "tags.title": (filter: IArrayGridFilter) => {
      const tags = expandFilterValue(filter)
        .map((value) => escapeString(value.toLowerCase()))
        .join("|");

      return { tags: { $elemMatch: { title: { $regex: `(?i).*(${tags}).*` } } } };
    },
  };
};

export const filtersOverrideForOkrName = (): Record<string, RestLayerFilterFactory> => {
  return {
    name: (filter: ITextGridFilter) => {
      const escapedValue = escapeString(filter.value.toLowerCase());
      return { $or: [{ name: { $regex: escapedValue } }, { metrics: { $elemMatch: { name: { $regex: escapedValue } } } }] };
    },
  };
};

export const goalsGridPersistedScreenPath = (sessionId: string): string[] => goalsPersistedScreenPath(sessionId, "grid");
export const goalsPersistedScreenPath = (sessionId: string, screenName: string): string[] => ["goals", screenName, sessionId];
export const expandedGoalsScreenPath = (sessionId: string): string[] => goalsPersistedScreenPath(sessionId, "alignment");
export const goalsViewOptionsPath = ["goals"];
